import React, { useEffect, useState } from 'react'
import {
  Grid,
  Typography,
  CardContent,
  Card,
  Box,
  Checkbox,
  Stack,
  Tooltip,
  Button,
  Slider,
  List,
  ListItem,
  IconButton,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { ClassNameMap, withStyles } from '@mui/styles'
import EditIcon from '@mui/icons-material/Edit'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import SearchIcon from '@mui/icons-material/Search'
import SyncIcon from '@mui/icons-material/Sync'
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import stylesheet from '../detail-container.stylesheet'
import CardLayoutComponent from '../common/card-layout.component'
import EmptyCardComponent from '../common/empty-card.component'
import ConditionsEvaluationsDialogComponent from '../common/conditions-evaluations-dialog.component'
import DiagnosisPlanActionsComponent from './diagnosis-plan-actions.component'
import UpdateDiagnosisStatusComponent from './update-diagnosis-status.component'
import { RootState } from '../../../../redux/store'
import { useUpdateDataAction } from '../../../../utils/update-details-hook'
import Helper from '../../../../utils/helper'
import { PatientService } from '../../../../services/patient.service'
import SearchConditionsComponent from './search-conditions.component'
import GradientCircleComponent from '../common/gradient-circle.component'
import PrioritizedConditionsPanel from '../../../questionnaires/PrioritizedConditionsPanel'
import { useThemeMode } from '../../../../contexts/ThemeContext'
import { adjustColorForDarkMode } from '../../../../utils/color-utils'
import { profiler } from '../../../../utils/profiler'

interface IProps {
  input: any
  classes: ClassNameMap
}

// Constants
const STAGES = {
  SCREENING: 'screening',
  // DIAGNOSIS_PRIORITIZATION retired — SCREENING transitions directly to
  // CONFIRMATORY_TESTING; condition selection now happens during SCREENING.
  CONFIRMATORY_TESTING: 'confirmatory testing',
  POST_DIAGNOSIS: 'post-diagnosis',
  EXITED_WITHOUT_DIAGNOSIS: 'exited without diagnosis'
} as const

const DiagnosisPlanComponent: React.FC<IProps> = ({ input, classes }) => {
  // Theme
  const { mode } = useThemeMode()
  const isDarkMode = mode === 'dark'

  // State
  const [diaPlan, setDiaPlan] = useState([] as any)
  const [isConditionEval, setIsConditionEval] = useState(false)
  const [loading, setLoading] = useState(false)
  const [prgCondition, setPrgCondition] = useState(null) as any
  const [currConditionPrg, setCurrConditionPrg] = useState('') as any
  const [screeningApprove, setScreeningApprove] = useState(null as any)
  const [conditionApprovalList, setConditionApprovalList] = useState([] as any)
  const [isUpdate, setIsUpdate] = useState(null as any)
  const [currDiagnosis, setCurrDiagnosis] = useState({} as any)
  const [openConditionEval, setOpenConditionEval] = useState(false)
  const [conditionsData, setConditionsData] = useState({} as any)
  const [showPrioritizedPanel, setShowPrioritizedPanel] = useState(false)
  const [prioritizedPanelData, setPrioritizedPanelData] = useState<any[]>([])
  const [autoSelectAttempted, setAutoSelectAttempted] = useState<Set<string>>(new Set()) // Track auto-selection per problem

  // Selectors
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const programEnrollments = useSelector(
    (state: RootState) => state.patientDetail.patientDetail?.program_enrollments || []
  )
  const multiProgramConfirm = useSelector((state: RootState) => state.multiProgramConfirm)
  const patientDetailStatus: any = useSelector((state: RootState) => state.patientDetail.status)

  // Services
  const patientService = new PatientService()
  const { updatePatientData, updateDiagnosisData } = useUpdateDataAction(patientDetail?.id)

  // Auto-select top conditions for non-AIDA evaluation programs
  // Selects top 5 by probability + top 2 by urgency (deduplicated)
  const autoSelectConditions = async (diagnosisPlan: any[]) => {
    if (Helper.isAIDA() || !patientDetail?.id) return

    for (const plan of diagnosisPlan) {
      const problemId = plan.problem_id
      const conditions = plan.problem_conditions || []

      // Skip if already attempted auto-selection for this problem
      if (autoSelectAttempted.has(problemId)) continue

      // Skip if any conditions are already selected
      const hasSelectedConditions = conditions.some((c: any) => c.selected_for_evaluation)
      if (hasSelectedConditions) continue

      // Skip if no conditions available
      if (conditions.length === 0) continue

      // Mark as attempted
      setAutoSelectAttempted(prev => new Set(prev).add(problemId))

      // Sort by probability (descending) and get top 5
      const byProbability = [...conditions]
        .sort((a: any, b: any) => (b.probability || 0) - (a.probability || 0))
        .slice(0, 5)

      // Sort by urgency (descending) and get top 2 not already in probability list
      const probabilityIds = new Set(byProbability.map((c: any) => c.condition_id))
      const byUrgency = [...conditions]
        .filter((c: any) => !probabilityIds.has(c.condition_id))
        .map((c: any) => ({ ...c, urgencyNum: parseInt(c.urgency || '0') || 0 }))
        .sort((a: any, b: any) => b.urgencyNum - a.urgencyNum)
        .slice(0, 2)

      // Combine and deduplicate
      const conditionsToSelect = [...byProbability, ...byUrgency]

      if (conditionsToSelect.length === 0) continue

      // Build the payload for API
      const conditionPayload = conditionsToSelect.map((c: any) => ({
        condition_id: c.condition_id,
        selected_for_evaluation: true
      }))

      try {
        await patientService.postConditionsEval(
          patientDetail.id, problemId, { problem_conditions: conditionPayload }
        )
        // Trigger full recalculation (same as recalculate button)
        // This ensures recommended questionnaires and diagnostic thresholds are populated
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
      } catch (err) {
        console.error('Failed to auto-select conditions:', err)
      }
    }
  }

  // Effects
  useEffect(() => {
    const problemConditions = patientDetail?.diagnosis?.diagnosis_plan?.map((condition: any) => ({
      ...condition,
      mode: 'view'
    })) || []
    const selectedConditionsForEvaluation = problemConditions?.filter(
      (condition: any) => condition.selected_for_evaluation
    )

    setDiaPlan(patientDetail?.diagnosis?.diagnosis_plan || [])
    setConditionApprovalList(selectedConditionsForEvaluation)
    setLoading(false)

    // Auto-select conditions is now handled by the backend on first load
    // Frontend auto-selection disabled to avoid double API calls and lag
    // See blueprint.py: AUTO_SELECT_CONDITIONS_ON_FIRST_LOAD
  }, [patientDetail])

  const getProgramStage = (program: string) => {
    return programEnrollments.find((enrollment: any) => enrollment.program === program)?.current_stage
  }

  const getCardOutline = (program: string) => {
    const rawColor = programEnrollments.find((enrollment: any) => enrollment.program === program)?.color_code || ''
    return adjustColorForDarkMode(rawColor, isDarkMode)
  }

  const getProgramPendingCount = (program: string) => {
    return multiProgramConfirm.find((item: any) => item.program === program)?.pending || 0
  }

  const getCarePlanTitle = (program: string) => {
    const programDisplayName = programEnrollments.find(
      (enrollment: any) => enrollment.program === program
    )?.program_display_name || ''
    return programDisplayName
  }

  const isPendingStatus = (id: string) => {
    const diagnosisStatus = patientDetail?.diagnosis?.diagnosis_plan?.find(
      (item: any) => item.id === id
    )?.diagnosis_status || ''
    return diagnosisStatus === 'pending'
  }

  const isMoveStageToPostDiagnosis = (problemId: string) => {
    const problemConditions = patientDetail?.diagnosis?.problem_conditions || []
    const totalConditions = problemConditions.filter(
      (condition: any) => condition.problem_id === problemId
    )
    const evaluatedConditions = totalConditions.filter(
      (condition: any) => condition.selected_for_evaluation
    )
    return totalConditions.length === evaluatedConditions.length
  }

  const filterConditions = (planData: any[], isSearch: boolean) => {
    let dataWithNumericUrgency = planData
      ?.filter((item: any) => item.selected_for_evaluation !== true)
      ?.map((item: any) => ({ ...item, urgencyNum: parseInt(item.urgency || '0') || 0 }))

    const topByUrgency = [...dataWithNumericUrgency]?.sort((a, b) => b.urgencyNum - a.urgencyNum)
    const topByProbability = [...dataWithNumericUrgency]?.sort((a, b) => (b.probability || 0) - (a.probability || 0))

    // Get top 3 from probability
    const topProbability = topByProbability.slice(0, 3)
    const probabilityIds = new Set(topProbability.map(item => item.condition_id))

    // Get top 3 from urgency, excluding condition_ids that exist in topProbability
    const topUrgencyFiltered = topByUrgency.filter(item => !probabilityIds.has(item.condition_id)).slice(0, 3)

    const uniqueResults = [...topProbability, ...topUrgencyFiltered]

    if (!isSearch) {
      const selectedForEvaluation = planData
        ?.filter((item: any) => item.selected_for_evaluation)
        ?.map((item: any) => ({ ...item, urgencyNum: parseInt(item.urgency || '0') || 0 }))

      const selectedUniqueMap = new Map()
      selectedForEvaluation.forEach(item => {
        if (!selectedUniqueMap.has(item.condition_id)) {
          selectedUniqueMap.set(item.condition_id, item)
        }
      })

      return [...uniqueResults, ...Array.from(selectedUniqueMap.values())]
    }

    return uniqueResults
  }

  const filterConditionsByType = (planData: any[], isSearch: boolean, type: string) => {
    // Return evaluation conditions sorted alphabetically
    if (type === 'evaluation') {
      return planData
        .filter((item: any) => item.selected_for_evaluation)
        .sort((a: any, b: any) =>
          a.condition_name.toLowerCase().localeCompare(b.condition_name.toLowerCase())
        );
    }

    // Filter non-evaluation items and add numeric urgency
    let dataWithNumericUrgency = [] as any
    if (type === 'probability') {
      dataWithNumericUrgency = planData
        .filter((item: any) => !item.selected_for_evaluation)
        .map((item: any) => ({
          ...item,
          urgencyNum: parseInt(item.urgency || '0') || 0
        }));
    } else {
      dataWithNumericUrgency = planData
        .filter((item: any) => !item.selected_for_evaluation && item.urgency > 6)
        .map((item: any) => ({
          ...item,
          urgencyNum: parseInt(item.urgency || '0') || 0
        }));
    }

    // Sort based on type
    const sortedData = type === 'probability'
      ? [...dataWithNumericUrgency].sort((a, b) => (b.probability || 0) - (a.probability || 0))
      : [...dataWithNumericUrgency].sort((a, b) => (b.urgencyNum || 0) - (a.urgencyNum || 0));

    // Remove duplicates based on condition_id and take top 6
    const uniqueResults = Array.from(
      new Map(sortedData.map(item => [item.condition_id, item])).values()
    ).slice(0, 5);

    return uniqueResults;
  };

  // Event handlers
  const handleConditionApprove = (event: any, item: any) => {
    event.stopPropagation()
    event.preventDefault()
    setIsConditionEval(true)
    setCurrDiagnosis({
      id: item.id,
      problem_id: item.problem_id,
      recommendation: item.recommendation,
      conditionApprovalList: item?.condApprovalList || [],
      currentProgram: item?.currentProgram || '',
      currStage: getProgramStage(item.programs?.[0] || '')
    })
  }

  const handleCloseConditionDialog = () => {
    setLoading(false)
    setIsConditionEval(false)
    setCurrDiagnosis({})
    setConditionApprovalList([])
  }

  const handleScreeningClose = () => {
    setScreeningApprove(null)
    setCurrDiagnosis({})
  }

  const handleDiagnosisUpdate = (item: any, currentProgram: string) => {
    setIsUpdate(true)
    setCurrDiagnosis({ ...item, currPrg: currentProgram })
  }

  const closeUpdateDiagnosis = () => {
    setIsUpdate(false)
    setCurrDiagnosis({})
  }

  const closeApprovalDialog = () => {
    setLoading(false)
    if (currDiagnosis?.currStage === STAGES.SCREENING) {
      handleScreeningClose()
    } else {
      handleCloseConditionDialog()
    }
  }

  const updateConditionEvalStatus = async (problemId: string, conditionObj: any[], currPrg: string) => {
    const currentProgram = currPrg || currDiagnosis?.currPrg || ''
    if (!problemId || !patientDetail?.id) return
    try {
      profiler.start('postConditionsEval API call')
      setLoading(true)
      const response = await patientService.postConditionsEval(
        patientDetail.id, problemId, { problem_conditions: conditionObj }
      )
      profiler.end('postConditionsEval API call')
      if (response?.data) {
        if (currentProgram) {
          setLoading(false)
          setPrgCondition(null)
          recalculateDiagnosisDetails(currentProgram)
          return
        } else {
          profiler.start('update local diaPlan state')
          const latestObj = response?.data?.problem_conditions || []
          if (latestObj.length > 0) {  // Update local state with the latest problem conditions
            const updatedDiaPlan = diaPlan?.map((item: any) => {
              if (item.problem_id === problemId) {
                const oldConditions = item?.problem_conditions || []
                const updatedConditions = oldConditions.map((i: any) => {
                  const matched = conditionObj.find((c: any) => c.condition_id === i.condition_id)
                  return {
                    ...i,
                    selected_for_evaluation: matched ? matched.selected_for_evaluation : i.selected_for_evaluation
                  }
                })
                return {
                  ...item,
                  problem_conditions: updatedConditions
                }
              }
              return item
            })
            setDiaPlan(updatedDiaPlan)
          }
          profiler.end('update local diaPlan state')
        }
        profiler.start('updatePatientData (total)')
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true, flowName: 'Condition Eval Update' })
      }
      setLoading(false)
      setPrgCondition(null)

      if (currDiagnosis?.currStage !== STAGES.CONFIRMATORY_TESTING) {
        handleCloseConditionDialog()
      }
    } catch (error: any) {
      profiler.end('postConditionsEval API call')
      if (currDiagnosis?.currStage !== STAGES.CONFIRMATORY_TESTING)
        handleCloseConditionDialog()
      toast.error(Helper.getErrorText(error), Helper.bottom_center())
      setLoading(false)
      setPrgCondition(null)
    }
  }

  const confirmScreening = async (type: string) => {
    if (!patientDetail?.id) return

    profiler.startFlow(`Confirm Screening (${type})`)
    profiler.start('setLoading(true)')
    setLoading(true)
    profiler.end('setLoading(true)')

    const payload = { screening_approval_status: type }
    const planId = currDiagnosis.currStage === STAGES.SCREENING
      ? currDiagnosis.id
      : currDiagnosis.problem_id

    try {
      profiler.start('postDiagnosisPlan API call')
      const response = await patientService.postDiagnosisPlan(patientDetail.id, planId, payload)
      profiler.end('postDiagnosisPlan API call')

      if (response?.data) {
        profiler.start('updatePatientData (total)')
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true, flowName: `Confirm Screening (${type})` })
      }

      closeApprovalDialog()
    } catch (error: any) {
      profiler.end('postDiagnosisPlan API call')
      closeApprovalDialog()
      toast.error(Helper.getErrorText(error), Helper.bottom_center())
    }
  }

  const confirmConditionEval = (type = '') => {
    if (!currDiagnosis?.currStage) return

    setLoading(true)
    if (type === 'Rejected') {
      confirmScreening(type)
      return
    }

    // Condition approval used to fire only at the retired DIAGNOSIS_PRIORITIZATION
    // stage; now condition selection lives inside SCREENING, so the same dispatch
    // applies whenever the user submits selections from the condition dialog
    // regardless of stage.
    if (currDiagnosis?.conditionApprovalList?.length > 0) {
      const conApprovalList = currDiagnosis.conditionApprovalList
      const conditionObj = conApprovalList.map((condition: any) => ({
        condition_id: condition.condition_id,
        selected_for_evaluation: Boolean(condition.selected_for_evaluation),
        cond_approval_status: condition.selected_for_evaluation ? "Approved" : null
      }))

      if (conditionObj.length > 0) {
        updateConditionEvalStatus(conApprovalList[0].problem_id, conditionObj, '')
      }
    }

    handleCloseConditionDialog()
  }

  const handleConditionEval = (checked: boolean, condition: any, currPrg = '') => {
    // Immediately update local state for responsive UI
    const updatedDiaPlan = diaPlan?.map((item: any) => {
      const updatedConditions = item?.problem_conditions?.map((c: any) =>
        c.condition_id === condition.condition_id
          ? { ...c, selected_for_evaluation: checked }
          : c
      )
      return {
        ...item,
        problem_conditions: updatedConditions
      }
    })
    setDiaPlan(updatedDiaPlan)

    // Then update conditionApprovalList and make API call
    const isConditionExists = conditionApprovalList.some(
      (item: any) => item.condition_id === condition.condition_id
    )

    let updatedConditionList: any[]
    updatedConditionList = !isConditionExists
      ? [...conditionApprovalList, { ...condition, selected_for_evaluation: checked }]
      : conditionApprovalList.map((item: any) =>
        item.condition_id === condition.condition_id
          ? { ...item, selected_for_evaluation: checked }
          : item
      )
    setConditionApprovalList(updatedConditionList)

    // API call in background
    const conditionsForEvaluation = updatedConditionList.map((item: any) => ({
      condition_id: item.condition_id,
      selected_for_evaluation: Boolean(item.selected_for_evaluation)
    }))

    if (conditionsForEvaluation.length > 0) {
      const problemId = updatedConditionList[0]?.problem_id
      updateConditionEvalStatus(problemId, conditionsForEvaluation, currPrg)
      setPrgCondition(currPrg)
    }
  }

  const handleAutoSelect = async (currentProgram: string) => {
    if (!patientDetail?.id) return
    const enrollment = programEnrollments.find((e: any) => e.program === currentProgram)
    if (!enrollment?.enrollment_id) {
      toast.error('No enrollment found for this program', Helper.bottom_center())
      return
    }
    try {
      setLoading(true)
      setPrgCondition(currentProgram)
      const response = await patientService.autoSelectConditions(patientDetail.id, enrollment.enrollment_id)
      if (response?.data) {
        toast.success(
          `Auto-selected ${response.data.selected_count} conditions (${response.data.updated_count} updated)`,
          Helper.bottom_center()
        )
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
      }
    } catch (error: any) {
      toast.error(Helper.getErrorText(error), Helper.bottom_center())
    } finally {
      setLoading(false)
      setPrgCondition(null)
    }
  }

  const changeStageByName = async (stage: string, currentProgram: any) => {
    if (!patientDetail?.id || !currentProgram || !stage) return

    profiler.startFlow(`Change Stage to ${stage}`)
    profiler.start('setLoading(true)')
    setLoading(true)
    profiler.end('setLoading(true)')

    profiler.start('changeStageByName API call')
    patientService.changeStageByName(patientDetail.id, currentProgram, stage)
      ?.then((response: any) => {
        profiler.end('changeStageByName API call')
        if (response?.data) {
          profiler.start('updatePatientData (total)')
          updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true, flowName: `Change Stage to ${stage}` })
        }
      }).catch((error: any) => {
        profiler.end('changeStageByName API call')
        toast.error(Helper.getErrorText(error), Helper.bottom_center())
      }).finally(() => {
        setLoading(false)
      })
  }

  // Render helpers
  const renderProbability = (probability: number) => {
    if (!probability) return ''

    const displayValue = probability < 0.1 ? '<0.1' : probability

    return (
      <Tooltip title={<small>{`Probability ${probability}%`}</small>}>
        <Typography component="span" pl={0.2} color="textSecondary" variant="subtitle2" className={classes.subHeaderTitle}>
          <small>{`(${displayValue}%)`}</small>
        </Typography>
      </Tooltip>
    )
  }

  const renderPriority = (baselineIndex: number, priority: number, urgency: number) => {
    return <GradientCircleComponent
      baselineIndex={baselineIndex}
      priority={priority}
      urgency={parseInt(String(urgency || 0))}
    />
  }

  const setIcons = () => input.isActionItems ? [] : [{ iconType: "more", callback: {} },]

  // A heading over an empty list is noise, not information — an empty group
  // renders nothing at all rather than a "not available" placeholder. Applies to
  // every profile; a nurse reading a prioritized-data view should see only
  // groups that actually have conditions in them.
  const renderConditionGroup = (
    label: string, conditionList: any[], currentStage: string, currentProgram: string, type: string,
  ) => {
    if ((filterConditionsByType(conditionList, false, type) || []).length < 1) return null

    return (
      <>
        <Typography variant="subtitle2" className={classes.subHeaderTitle} pt={0.2} pb={0.2}>
          {label}
        </Typography>
        {renderProblemConditions(conditionList, currentStage, currentProgram, type)}
      </>
    )
  }

  const renderProblemConditions = (conditionList: any[], currentStage: string, currentProgram: string, type: string) => {
    const filteredConditions = filterConditionsByType(conditionList, false, type)
    if ((filteredConditions || [])?.length < 1)
      return <Typography variant="body2" className={classes.desc} pl={0.8} pt={0.3}>
        <small>{`Conditions with ${type === 'urgency' ? 'urgency >6' : 'high probability'} are not available`}</small>
      </Typography>

    return filteredConditions
      ?.sort((a: any, b: any) => a.probability < b.probability ? 1 : -1)
      ?.map((condition: any, key: number) => (
        <React.Fragment key={`dia-condition-${key}`}>
          <Box pb={0.1} className={classes.customMiniCard}>
            <Card variant="outlined" className={classes.compactCard}>
              <CardContent className={classes.zeroPadding}>
                <Stack direction="row" spacing={0} justifyContent="center" alignItems="center">
                  <Checkbox
                    sx={{ padding: '0rem !important', transform: "scale(0.55)", height: '0.9rem' }}
                    name="task_status"
                    disabled={input?.isActionItems || loading}
                    checked={condition.selected_for_evaluation}
                    onChange={(event, checked) => {
                      event.preventDefault()
                      event.stopPropagation()
                      handleConditionEval(checked, condition, currentProgram)
                    }}
                  />
                  <CardLayoutComponent
                    id={condition.id}
                    name={condition.condition_id}
                    leftHeader={condition.condition_name || condition.condition_id}
                    rightHeader=""
                    subHeader={renderProbability(condition.probability || 0)}
                    bottomText={renderPriority(condition?.baseline_index || 0, condition.priority || 0, condition.urgency)}
                    input={{
                      dependent_questionnaires: condition?.dependent_questionnaires || [],
                      tests_per_cond: condition?.tests_per_cond || [],
                      threshold: Object.keys(condition?.current_rationale || {}),
                      urgency: (condition.urgency || 0),
                      synopsis: condition?.synopsis || ''
                    }}
                    needs_confirmation={condition.needs_confirmation}
                    needs_update={condition.needs_update}
                    icons={setIcons()}
                    programs={condition.programs || [currentProgram]}
                    overridenPrograms={condition.overridden_programs}
                    onRefClick={() => null}
                    is_referred={condition.is_referred}
                    is_overridden={condition.is_overridden}
                    entityRef="problem_conditions"
                    patientId={patientDetail.id}
                    category="conditions"
                  />
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </React.Fragment>
      ))
  }

  const renderConditions = (conditions: any[], currentStage: string, currentProgram: string) => {
    const isLargeDifferential = (conditions?.length || 0) > 15

    if (isLargeDifferential) {
      return renderGroupedConditions(conditions, currentStage, currentProgram)
    }

    // Compact flat rendering for small differentials (AIDA / amyloidosis with ~6 conditions)
    return (
      <Box className={classes.customCard} sx={{ width: '98%', padding: '0.1rem 0.5rem 0rem 0rem !important' }}>
        {conditions?.length > 0
          ? <Stack spacing={0} alignItems={'flex-start'} justifyContent={'center'}>
            {conditions.some((cond: any) => cond.selected_for_evaluation) &&
              renderProblemConditions(conditions, currentStage, currentProgram, 'evaluation')
            }
            {renderConditionGroup(
              'Recommended conditions with high probability',
              conditions, currentStage, currentProgram, 'probability',
            )}
            {renderConditionGroup(
              'High urgency conditions',
              conditions, currentStage, currentProgram, 'urgency',
            )}
          </Stack>
          : <EmptyCardComponent />
        }
      </Box>
    )
  }

  const TOP_N = 8

  const LazyAccordion: React.FC<{
    title: string, count: number, color: string, children: () => React.ReactNode
  }> = ({ title, count, color, children }) => {
    const [hasOpened, setHasOpened] = useState(false)
    const accordionSx = {
      width: '100%',
      boxShadow: 'none',
      '&:before': { display: 'none' },
      backgroundColor: 'transparent',
    }
    const summarySx = { minHeight: 24, px: 0, '& .MuiAccordionSummary-content': { margin: '2px 0' } }
    return (
      <Accordion
        defaultExpanded={false}
        disableGutters
        sx={accordionSx}
        onChange={(_, expanded) => {
          if (expanded) setHasOpened(true)
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.8rem !important' }} />} sx={summarySx}>
          <Typography variant="subtitle2" className={classes.subHeaderTitle} sx={{ color, fontWeight: 600 }}>
            {title} ({count})
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>{hasOpened && children()}</AccordionDetails>
      </Accordion>
    )
  }

  // Get most_characteristic from either the plan item or the diagnosis-level data
  const mostCharacteristic = patientDetail?.diagnosis?.most_characteristic || []

  const renderGroupedConditions = (conditions: any[], currentStage: string, currentProgram: string) => {
    const sorted = [...conditions].sort((a: any, b: any) => (b.probability || 0) - (a.probability || 0))

    // Top conditions by probability (all types)
    const topByProb = sorted.slice(0, TOP_N)
    const topByProbIds: Record<number, boolean> = {}
    topByProb.forEach((c: any) => { topByProbIds[c.id] = true })

    // Top cardiac conditions by probability (standalone, may overlap with top-by-prob)
    const cardiac = sorted
      .filter((c: any) => c.is_cardiac)
      .slice(0, TOP_N)

    // Top urgent conditions by probability (standalone, may overlap with other sections)
    const urgent = sorted
      .filter((c: any) => {
        const urg = parseInt(String(c.urgency || '0')) || 0
        return urg > 6
      })
      .slice(0, TOP_N)
    // Remaining conditions (everything not in top-by-probability)
    const remaining = sorted.filter((c: any) => !topByProbIds[c.id])

    return (
      <Box className={classes.customCard} sx={{ width: '98%', padding: '0.1rem 0.5rem 0rem 0rem !important' }}>
        {conditions?.length > 0
          ? <Stack spacing={0} alignItems={'flex-start'} justifyContent={'center'} sx={{ width: '100%' }}>
            {/* Top by probability — always visible */}
            {topByProb.length > 0 && (
              <>
                <Typography variant="subtitle2" className={classes.subHeaderTitle} pt={0.2} pb={0.2}
                  sx={{ color: '#1976d2', fontWeight: 600 }}>
                  Top conditions by probability ({topByProb.length})
                </Typography>
                {topByProb.map((condition: any, key: number) => (
                  <React.Fragment key={`top-prob-${key}`}>
                    {renderConditionRow(condition, currentStage, currentProgram)}
                  </React.Fragment>
                ))}
              </>
            )}

            {/* Most characteristic — lazy collapsed */}
            {mostCharacteristic.length > 0 && (
              <LazyAccordion title="Most characteristic" count={mostCharacteristic.length} color="#2e7d32">
                {() => mostCharacteristic.map((item: any, key: number) => {
                  // Match to a problem condition so we can render the same row as other sections
                  const matched = conditions?.find((c: any) => c.indication === item.condition)
                  if (!matched) return null
                  return (
                    <React.Fragment key={`mc-${key}`}>
                      {renderConditionRow(matched, currentStage, currentProgram)}
                    </React.Fragment>
                  )
                })}
              </LazyAccordion>
            )}

            {/* Top cardiac — lazy collapsed */}
            {cardiac.length > 0 && (
              <LazyAccordion title="Top cardiac conditions" count={cardiac.length} color="#d32f2f">
                {() => cardiac.map((condition: any, key: number) => (
                  <React.Fragment key={`top-cardiac-${key}`}>
                    {renderConditionRow(condition, currentStage, currentProgram)}
                  </React.Fragment>
                ))}
              </LazyAccordion>
            )}

            {/* Top urgent — lazy collapsed */}
            {urgent.length > 0 && (
              <LazyAccordion title="Top urgent conditions" count={urgent.length} color="#ed6c02">
                {() => urgent.map((condition: any, key: number) => (
                  <React.Fragment key={`top-urgent-${key}`}>
                    {renderConditionRow(condition, currentStage, currentProgram)}
                  </React.Fragment>
                ))}
              </LazyAccordion>
            )}

            {/* All remaining — lazy collapsed */}
            {remaining.length > 0 && (
              <LazyAccordion title="Other conditions" count={remaining.length} color="#757575">
                {() => remaining.map((condition: any, key: number) => (
                  <React.Fragment key={`remaining-${key}`}>
                    {renderConditionRow(condition, currentStage, currentProgram)}
                  </React.Fragment>
                ))}
              </LazyAccordion>
            )}
          </Stack>
          : <EmptyCardComponent />
        }
      </Box>
    )
  }

  const renderConditionRow = (condition: any, currentStage: string, currentProgram: string) => (
    <Box pb={0.1} className={classes.customMiniCard}>
      <Card variant="outlined" className={classes.compactCard}>
        <CardContent className={classes.zeroPadding}>
          <Stack direction="row" spacing={0} justifyContent="center" alignItems="center">
            <Checkbox
              sx={{ padding: '0rem !important', transform: "scale(0.55)", height: '0.9rem' }}
              name="task_status"
              disabled={input?.isActionItems || loading}
              checked={condition.selected_for_evaluation}
              onChange={(event, checked) => {
                event.preventDefault()
                event.stopPropagation()
                handleConditionEval(checked, condition, currentProgram)
              }}
            />
            <CardLayoutComponent
              id={condition.id}
              name={condition.condition_id}
              leftHeader={condition.condition_name || condition.condition_id}
              rightHeader=""
              subHeader={renderProbability(condition.probability || 0)}
              bottomText={renderPriority(condition?.baseline_index || 0, condition.priority || 0, condition.urgency)}
              input={{
                dependent_questionnaires: condition?.dependent_questionnaires || [],
                tests_per_cond: condition?.tests_per_cond || [],
                threshold: Object?.keys(condition?.current_rationale) || [],
                urgency: (condition.urgency || 0),
                synopsis: condition?.synopsis || ''
              }}
              needs_confirmation={condition.needs_confirmation}
              needs_update={condition.needs_update}
              icons={input.isActionItems ? [] : [{ iconType: "more", callback: {} }]}
              programs={condition.programs || [currentProgram]}
              overridenPrograms={condition.overridden_programs}
              onRefClick={() => null}
              is_referred={condition.is_referred}
              is_overridden={condition.is_overridden}
              entityRef="problem_conditions"
              patientId={patientDetail.id}
              category="conditions"
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  )

  const printInstructions = (instructions: string | string[]) => {
    if (!instructions || (typeof instructions === 'object' && Object.keys(instructions).length === 0)) {
      return ''
    }

    if (typeof instructions === 'string') {
      return (
        <Box sx={{ padding: '0rem 0.5rem 0.2rem 0.1rem' }}>
          <Typography variant="subtitle2" className={classes.subHeaderTitle}>
            {instructions}
          </Typography>
        </Box>
      )
    }

    return (
      <List sx={{ padding: '0rem 0px 5px 0.1rem', marginTop: '-0.2rem' }}>
        {instructions.map((instruction, index) => (
          <ListItem className={classes.puList} key={`inst-${index}`}>
            <Typography variant="body2" component="span" className={classes.evalBoxVal}>
              {instruction}
            </Typography>
          </ListItem>
        ))}
      </List>
    )
  }

  const autoSelectButton = (currentProgram: string) => {
    return (
      <Tooltip title="Auto-select top conditions by probability and urgency">
        <Button
          variant="text"
          className={classes.expressModeBtn}
          sx={{
            padding: '0rem 0rem 0rem 0.1rem !important',
            minWidth: '0.1rem',
            '&.Mui-disabled': { border: 'none !important' }
          }}
          disabled={patientDetailStatus === 'loading' || loading}
          onClick={(e: any) => {
            e.preventDefault()
            e.stopPropagation()
            handleAutoSelect(currentProgram)
          }}
        >
          <AutoFixHighIcon className={classes.editDefIcon} />
        </Button>
      </Tooltip>
    )
  }

  const searchConditionEval = (conditions: any, problem_id: any) => {
    return <Button variant="text" title='Edit' className={classes.expressModeBtn}
      sx={{
        padding: '0rem 0rem 0rem 0.1rem !important',
        minWidth: '0.1rem',
        '&.Mui-disabled': { border: 'none !important', }
      }}
      disabled={patientDetailStatus === 'loading'}
      onClick={(e: any) => {
        e.preventDefault();
        e.stopPropagation()
        setOpenConditionEval(true)
        setConditionsData({
          problem_id: problem_id,
          conditions: conditions || [],
          recommendedConditions: filterConditions(conditions, false) // filterConditions(conditions || [], true)
        })
      }}
    >
      <SearchIcon className={classes.subHeadername} sx={{ fill: openConditionEval ? 'var(--color-primary)' : 'inhert' }} />
    </Button>
  }

  const reviewPrioritizedConditions = (conditions: any[]) => {
    const hasPrioritized = conditions?.some((c: any) => c.selected_for_evaluation)
    return (
      <Tooltip title={hasPrioritized ? 'Review prioritized conditions' : 'No conditions prioritized'}>
        <span>
          <Button
            variant="text"
            className={classes.expressModeBtn}
            sx={{
              padding: '0rem 0rem 0rem 0.1rem !important',
              minWidth: '0.1rem',
              '&.Mui-disabled': { border: 'none !important', opacity: 0.4 }
            }}
            disabled={patientDetailStatus === 'loading' || !hasPrioritized}
            onClick={(e: any) => {
              e.preventDefault()
              e.stopPropagation()
              handleOpenPrioritizedPanel(conditions)
            }}
          >
            <PlaylistPlayIcon
              className={classes.subHeadername}
              sx={{ fill: showPrioritizedPanel ? 'var(--color-primary)' : 'inherit' }}
            />
          </Button>
        </span>
      </Tooltip>
    )
  }

  const recalculateDiagnosisDetails = async (currentProgram: any) => {
    profiler.startFlow('Evaluation Recalculate')
    profiler.start('setLoading(true)')
    setLoading(true)
    profiler.end('setLoading(true)')
    // Always use updatePatientData to ensure complete data including conds_per_test for diagnostic tests
    profiler.start('updatePatientData (total)')
    try {
      await updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true, flowName: 'Evaluation Recalculate' })
    } finally {
      setLoading(false)
    }
  }

  const recalculateConditions = (currentProgram: any) => {
    return <Tooltip title='Recalculate'>
      <Button variant="text" className={classes.expressModeBtn}
        sx={{
          padding: '0rem 0rem 0rem 0.1rem !important',
          minWidth: '0.01rem',
          '&.Mui-disabled': { border: 'none !important', }
        }}
        disabled={patientDetailStatus === 'loading'}
        onClick={(e: any) => {
          e.preventDefault();
          e.stopPropagation()
          recalculateDiagnosisDetails(currentProgram)
        }}
      >
        {(loading && prgCondition === currentProgram) || patientDetailStatus === 'dia_loading'
          ? <CircularProgress color='inherit' size={11} />
          : <SyncIcon className={classes.editDefIcon} />
        }
      </Button>
    </Tooltip>
  }

  // Open prioritized conditions panel
  const handleOpenPrioritizedPanel = (problemConditions: any[]) => {
    const prioritizedConditions = problemConditions
      ?.filter((c: any) => c.selected_for_evaluation)
      ?.sort((a: any, b: any) => (b.probability || 0) - (a.probability || 0))
      ?.map((c: any) => ({
        condition_id: c.condition_id,
        condition_name: c.condition_name || c.condition_id,
        condition_display_name: c.condition_display_name || c.condition_name || c.condition_id,
        probability: c.probability || 0,
        urgency: parseInt(c.urgency || '0') || 0,
        dependent_questionnaires: c.dependent_questionnaires || []
      }))

    if (prioritizedConditions?.length > 0) {
      setPrioritizedPanelData(prioritizedConditions)
      setShowPrioritizedPanel(true)
    } else {
      toast.info('No conditions selected for evaluation yet', Helper.bottom_center())
    }
  }

  const handleClosePrioritizedPanel = () => {
    setShowPrioritizedPanel(false)
    setPrioritizedPanelData([])
  }

  const handleProbabilityUpdate = (conditionId: string, newProbability: number) => {
    // Update probability in panel data (will be refreshed from server after questionnaire save)
    setPrioritizedPanelData(prev =>
      prev.map(c =>
        c.condition_id === conditionId
          ? { ...c, probability: newProbability }
          : c
      ).sort((a, b) => (b.probability || 0) - (a.probability || 0))
    )
  }

  const handleConditionEvalClose = (conditionsObj: any[] = []) => {
    setOpenConditionEval(false)
    setConditionsData({})

    // Early return if no conditions to process
    if (conditionsObj.length === 0) return

    const updatedDiaPlan = diaPlan?.map((item: any) => {
      if (item.problem_id === conditionsObj[0]?.problem_id) {
        const oldConditions = item?.problem_conditions || []

        // Create a map of condition_id to condition for quick lookup
        const conditionMap = new Map(conditionsObj.map(c => [c.condition_id, c]))

        // Update existing conditions
        const updatedExisting = oldConditions?.map((condition: any) =>
          conditionMap.has(condition.condition_id)
            ? conditionMap.get(condition.condition_id)
            : condition
        )

        // Find new conditions that don't exist in oldConditions
        const existingIds = new Set(oldConditions.map((c: any) => c.condition_id))
        const newConditions = conditionsObj.filter(c => !existingIds.has(c.condition_id))

        return {
          ...item,
          problem_conditions: [...updatedExisting, ...newConditions]
        }
      }
      return item
    })

    setDiaPlan(updatedDiaPlan)
  }

  const getDiagnosisStatusSection = (item: any, currentProgram: string) => (
    <Box>
      <Box className={classes.customCard} sx={{ width: '99%' }}>
        <Card variant="outlined">
          <CardContent sx={{ padding: '0px 2px 0px 5px !important', backgroundColor: 'var(--color-primary-dark) !important' }}>
            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between" sx={{ textShadow: '0px 1px 1px rgba(0,0,0,0.25)', fontWeight: 600, fontSize: '0.82rem' }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography pl={0.2} className={classes.desc} variant="body2" sx={{ color: '#FFF !important', whiteSpace: 'nowrap', fontWeight: 600 }}>
                  Diagnosis
                </Typography>
                {item?.date_evaluated &&
                  <Typography variant="caption" sx={{ color: '#FFF !important', fontWeight: 600, fontSize: '0.63rem', whiteSpace: 'nowrap', paddingTop: '1px' }}>
                    {item.date_evaluated}
                  </Typography>
                }
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Typography className={classes.desc} variant="body2" sx={{ color: '#FFF !important', wordBreak: 'break-word', fontWeight: 600 }}>
                  {item?.diagnosis_status || ''}
                </Typography>
                <IconButton
                  sx={{ fill: '#FFF !important', padding: '0.1rem !important', }}
                  onClick={() => handleDiagnosisUpdate(item, currentProgram)}
                >
                  <EditIcon sx={{ fill: '#FFF !important' }} className={classes.editIcon} />
                </IconButton>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  )

  const getProceedNextButton = (item: any, currentProgram: string, currentStage: string) => {
    const condApprovalList = item?.problem_conditions?.filter((c: any) => c.selected_for_evaluation) || []

    if (currentStage === STAGES.CONFIRMATORY_TESTING) {
      return (
        <Tooltip title={isPendingStatus(item.id) ? 'Cannot proceed while diagnosis status is pending.' : ''}>
          <Button
            variant="outlined"
            sx={{
              marginTop: '0rem !important',
              minWidth: (isMoveStageToPostDiagnosis(item.problem_id) &&
                patientDetail?.pending_task_counts?.[currentProgram] === 0)
                ? '7rem !important'
                : '2rem !important'
            }}
            className={classes.diagnosisPlanBtn}
            disabled={patientDetailStatus === 'loading' || input?.isActionItems || isPendingStatus(item.id)}
            onClick={() => changeStageByName(STAGES.POST_DIAGNOSIS, currentProgram)}
          >
            <Typography className={classes.diagnosisPlanBtnLabel} variant="body2">
              <small>Proceed to next step</small>
            </Typography>
          </Button>
        </Tooltip>
      )
    }

    if (currentStage === STAGES.SCREENING
      && item.needs_update
      && getProgramPendingCount(currentProgram) <= 1
    ) {
      return (
        <Button
          variant="outlined"
          className={classes.diagnosisPlanBtn}
          sx={{ width: 'auto !important', margin: '0rem 0.4rem 0rem 0rem !important' }}
          disabled={patientDetailStatus === 'loading' || input?.isActionItems}
          onClick={() => {
            setScreeningApprove(true)
            setCurrDiagnosis({
              id: item.id,
              problem_id: item.problem_id,
              recommendation: item.recommendation,
              currentProgram: currentProgram,
              currStage: currentStage
            })
          }}
        >
          <Typography className={classes.diagnosisPlanBtnLabel} variant="body2">
            <small>Proceed to next step</small>
          </Typography>
        </Button>
      )
    }

    // The DIAGNOSIS_PRIORITIZATION stage was retired. SCREENING approval now
    // transitions directly to CONFIRMATORY_TESTING via the backend stage
    // machine; the manual "advance after picking conditions" button that
    // lived here is no longer needed. The SCREENING approval flow above
    // is the sole gate.
    return null
  }

  const getDiagnosisPlan = () => {
    const plans = input?.actionProgram
      ? diaPlan?.filter((plan: any) => plan.programs?.includes(input?.actionProgram))
      : diaPlan
    return plans?.slice().sort((a: any, b: any) => {
      const nameA = programEnrollments.find((e: any) => e.program === a.programs?.[0])?.program_display_name || a.programs?.[0] || ''
      const nameB = programEnrollments.find((e: any) => e.program === b.programs?.[0])?.program_display_name || b.programs?.[0] || ''
      return nameA.toLowerCase().localeCompare(nameB.toLowerCase())
    })
  }

  return (
    <Grid container direction="row" spacing={1} className={classes.fullWidth}>
      <Grid item className={classes.fullWidth}>
        {getDiagnosisPlan()?.map((item: any) => {
          const currentProgram = item.programs?.[0] || ''
          const currentStage = getProgramStage(currentProgram)

          return (
            <Card
              key={`dia-plan-card-${item.problem_id}`}
              variant="outlined"
              sx={{
                border: `2px solid ${getCardOutline(currentProgram)}`,
                backgroundColor: 'transparent',
                // marginBottom: '8px'
              }}
            >
              <CardContent sx={{ padding: '0.1rem 0rem 0.2rem 0.25rem !important' }}>
                <Stack
                  spacing={1}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                // sx={{ marginTop: '-0.08rem !important' }}
                >
                  <Typography
                    variant="subtitle2"
                    className={classes.programTargetHeader}
                    sx={{ color: 'var(--color-primary)', padding: '0rem 0rem 0rem 0.1rem' }}
                  >
                    {getCarePlanTitle(currentProgram)}
                  </Typography>

                  <Stack direction="row" spacing={0.2} justifyContent="flex-end" alignItems="center" pb={0.4} pr={0.2}>
                    {getProceedNextButton(item, currentProgram, currentStage)}

                    {recalculateConditions(currentProgram)}

                    {autoSelectButton(currentProgram)}

                    {searchConditionEval(item?.problem_conditions, item?.problem_id)}

                    {reviewPrioritizedConditions(item?.problem_conditions)}

                    {![STAGES.EXITED_WITHOUT_DIAGNOSIS, STAGES.POST_DIAGNOSIS].includes(currentStage as any) && (
                      <DiagnosisPlanActionsComponent
                        input={{
                          currStage: currentStage,
                          isActionItems: input?.isActionItems,
                          program: currentProgram,
                          patient_id: patientDetail?.id,
                          plan_id: item?.id || '',
                          problem_id: item?.problem_id
                        }}
                      />
                    )}
                  </Stack>
                </Stack>

                {item?.instructions && printInstructions(item.instructions)}

                {getDiagnosisStatusSection(item, currentProgram)}

                {renderConditions(item?.problem_conditions || [], currentStage, currentProgram)}
              </CardContent>
            </Card>
          )
        })}
      </Grid>
      {/* Update Diagnosis Status Dialog */}
      <UpdateDiagnosisStatusComponent
        open={Boolean(isUpdate)}
        input={{
          title: 'Update diagnosis status',
          program: currDiagnosis?.currPrg,
          patientId: patientDetail.id,
          dId: currDiagnosis?.id,
          diagnosis_status: currDiagnosis?.diagnosis_status || ''
        }}
        handleDialogClose={closeUpdateDiagnosis}
      />

      {/* Condition Evaluation Dialog */}
      {isConditionEval && (
        <ConditionsEvaluationsDialogComponent
          open={isConditionEval}
          input={{
            title: `Condition evaluation - ${Helper.toCamelCaseFirstWord(currDiagnosis?.currStage)}`,
            data: currDiagnosis?.conditionApprovalList,
            stage: currDiagnosis?.currStage
          }}
          handleSubmit={confirmConditionEval}
          handleClose={handleCloseConditionDialog}
        />
      )}

      {/* Screening Approval Dialog */}
      {screeningApprove && (
        <ConditionsEvaluationsDialogComponent
          open={Boolean(screeningApprove)}
          input={{
            stage: currDiagnosis?.currStage,
            buttonLabel: 'Proceed to confirmatory testing',
            title: `Condition evaluation - ${Helper.toCamelCaseFirstWord(currDiagnosis?.currStage)}`,
            data: currDiagnosis?.recommendation
          }}
          handleSubmit={confirmScreening}
          handleClose={handleScreeningClose}
        />
      )}

      {openConditionEval && (
        <SearchConditionsComponent
          open={openConditionEval}
          input={{
            patient_id: patientDetail.id,
            title: 'Condition evaluations',
            problem_id: conditionsData?.problem_id,
            recommendedConditions: conditionsData?.recommendedConditions || [],
          }}
          item={conditionsData?.conditions || []}
          handleClose={handleConditionEvalClose}
        />
      )}

      {/* Prioritized Conditions Panel - tabbed view for rapid questionnaire entry */}
      <PrioritizedConditionsPanel
        open={showPrioritizedPanel}
        prioritizedConditions={prioritizedPanelData}
        onClose={handleClosePrioritizedPanel}
        onProbabilityUpdate={handleProbabilityUpdate}
      />
    </Grid>
  )
}

export default withStyles(stylesheet)(DiagnosisPlanComponent)